import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '@super/shared-types'
import type {
  Channel,
  ChannelSettings,
  UpdateChannelSettingsDto,
  ApiResponse,
} from '@super/shared-types'

export class ChannelSettingsApiClient extends BaseApiClient {
  /**
   * Get all channel settings for the current user's company
   * @returns Array of channel settings
   */
  async getAllChannelSettings(): Promise<ChannelSettings[]> {
    const response = await this.request<ApiResponse<ChannelSettings[]>>(
      API_ROUTES.CHANNEL_SETTINGS.GET_ALL,
    )
    return response.data || []
  }

  /**
   * Get channel settings for a specific channel
   * @param channel - The channel type (EMAIL, SMS, or CALL)
   * @returns Channel settings or default settings if none exist
   */
  async getChannelSettings(channel: Channel): Promise<ChannelSettings | null> {
    const response = await this.request<ApiResponse<ChannelSettings>>(
      interpolateRoute(API_ROUTES.CHANNEL_SETTINGS.GET, { channel }),
    )
    return response.data || null
  }

  /**
   * Update channel settings for a specific channel
   * @param channel - The channel type (EMAIL, SMS, or CALL)
   * @param data - The settings data to update
   * @returns Updated channel settings
   */
  async updateChannelSettings(
    channel: Channel,
    data: UpdateChannelSettingsDto,
  ): Promise<ChannelSettings | null> {
    const response = await this.request<ApiResponse<ChannelSettings>>(
      interpolateRoute(API_ROUTES.CHANNEL_SETTINGS.UPDATE, { channel }),
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )
    return response.data || null
  }

  /**
   * Get all channel settings for any company (SuperAdmin only)
   * @param companyId - The ID of the company
   * @returns Array of channel settings
   */
  async getAllChannelSettingsForCompany(
    companyId: string,
  ): Promise<ChannelSettings[]> {
    const response = await this.request<ApiResponse<ChannelSettings[]>>(
      `${API_ROUTES.ADMIN.CHANNEL_SETTINGS.GET_ALL}${this.buildQueryString({ companyId })}`,
    )
    return response.data || []
  }

  /**
   * Get channel settings for a specific channel and company (SuperAdmin only)
   * @param companyId - The ID of the company
   * @param channel - The channel type (EMAIL, SMS, or CALL)
   * @returns Channel settings or default settings if none exist
   */
  async getChannelSettingsForCompany(
    companyId: string,
    channel: Channel,
  ): Promise<ChannelSettings | null> {
    const response = await this.request<ApiResponse<ChannelSettings>>(
      `${interpolateRoute(API_ROUTES.ADMIN.CHANNEL_SETTINGS.GET, { channel })}${this.buildQueryString({ companyId })}`,
    )
    return response.data || null
  }

  /**
   * Update channel settings for any company (SuperAdmin only)
   * @param companyId - The ID of the company
   * @param channel - The channel type (EMAIL, SMS, or CALL)
   * @param data - The settings data to update
   * @returns Updated channel settings
   */
  async updateChannelSettingsForCompany(
    companyId: string,
    channel: Channel,
    data: UpdateChannelSettingsDto,
  ): Promise<ChannelSettings | null> {
    const response = await this.request<ApiResponse<ChannelSettings>>(
      `${interpolateRoute(API_ROUTES.ADMIN.CHANNEL_SETTINGS.UPDATE, { channel })}${this.buildQueryString({ companyId })}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )
    return response.data || null
  }
}

export const channelSettingsApiClient = new ChannelSettingsApiClient()
